/**
 * Vertical floor selector plus the map legend.
 *
 * The selector mirrors the physical stack — top of the list is the top of the
 * building — and shows the live unit count per level so an operator can see
 * where a filter actually bit.
 */

import React from 'react';
import { Store, DoorOpen } from 'lucide-react';
import { FLOORS, CATEGORY_COLORS, STATUS_COLORS } from '../data/mallData';

export function FloorSwitcher({ floor, onFloor, counts }) {
    // Top floor first, so the control reads like an elevator panel.
    const ordered = [...FLOORS].sort((a, b) => b.level - a.level);

    return (
        <div className="mm-floors" role="group" aria-label="Floor selector">
            {ordered.map((f) => {
                const c = counts[f.id] || { total: 0, visible: 0, vacant: 0 };
                const active = f.id === floor;
                return (
                    <button
                        key={f.id}
                        type="button"
                        className={`mm-floor${active ? ' is-active' : ''}`}
                        onClick={() => onFloor(f.id)}
                        aria-pressed={active}
                    >
                        <strong>{f.short}</strong>
                        <span className="mm-floor-name">{f.name}</span>
                        <span className="mm-floor-counts">
                            <em title="Units matching the current filter"><Store size={11} />{c.visible}</em>
                            {c.vacant > 0 && <em className="is-vacant" title="Vacant units"><DoorOpen size={11} />{c.vacant}</em>}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/**
 * One swatch standing for several colours.
 *
 * Un-categorised imported shapes keep the plan's own fills, so the row covers a
 * handful of colours rather than one. Hard stops rather than a blend, because a
 * gradient would invent colours the plan does not contain.
 */
function swatchOf(colors) {
    const shown = colors.slice(0, 4);
    if (shown.length === 1) return shown[0];
    const step = 100 / shown.length;
    const stops = shown
        .map((c, i) => `${c} ${i * step}%, ${c} ${(i + 1) * step}%`)
        .join(', ');
    return `linear-gradient(90deg, ${stops})`;
}

const HEAT_LABELS = {
    footfall: ['Quiet', 'Busy'],
    sales: ['Low density', 'High density'],
    risk: ['Secure', 'At risk'],
};

export function Legend({ heatmap, categoriesInView }) {
    if (heatmap !== 'none') {
        const [lo, hi] = HEAT_LABELS[heatmap] || ['Low', 'High'];
        return (
            <div className="mm-legend">
                <h5>{heatmap === 'footfall' ? 'Footfall density' : heatmap === 'sales' ? 'Sales density' : 'Vacancy risk'}</h5>
                <div className="mm-legend-ramp" />
                <div className="mm-legend-ends"><span>{lo}</span><span>{hi}</span></div>
                <p className="mm-legend-note">Normalised across the units on this floor.</p>
            </div>
        );
    }

    return (
        <div className="mm-legend">
            <h5>Categories on this floor</h5>
            <ul className="mm-legend-list">
                {categoriesInView.map((c) => {
                    // Tolerate the old shape (a bare category string) so this
                    // component stays usable outside the mall map page.
                    const entry = typeof c === 'string'
                        ? { key: c, label: c, colors: [CATEGORY_COLORS[c]], asDrawn: false }
                        : c;
                    return (
                        <li key={entry.key}>
                            {entry.asDrawn ? (
                                <span
                                    className="mm-cat-dot is-multi"
                                    style={{ background: swatchOf(entry.colors) }}
                                />
                            ) : (
                                <span className="mm-cat-dot" style={{ background: entry.colors[0] }} />
                            )}
                            {entry.label}
                        </li>
                    );
                })}
            </ul>
            <h5 className="mm-legend-sub">Status</h5>
            <ul className="mm-legend-list">
                {Object.keys(STATUS_COLORS).map((s) => (
                    <li key={s}>
                        <span className="mm-cat-dot" style={{ background: STATUS_COLORS[s] }} />
                        {s}
                    </li>
                ))}
            </ul>
        </div>
    );
}
